import { motion } from 'motion/react'
import { useId, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'

import { useMeasures, usePrograms, useSchemes } from '../api/hooks'
import { PROCESS_STEPS } from '../domain/labels'
import { TENANT_HOME } from './area'
import { deriveProgress, type ProcessProgress, type StepProgress } from './progress'

type Step = (typeof PROCESS_STEPS)[number]

const isActive = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`)

/** The Übersicht (dashboard) comes before step 1 and has no number (ticket 06). */
const OVERVIEW = { path: TENANT_HOME, label: 'Übersicht' } as const

/**
 * Progress from the cached list queries the step pages use anyway. Only rendered with a
 * selected municipality (see Header), so no request goes out without one.
 */
export function useProcessProgress(): ProcessProgress {
  const { data: measures } = useMeasures()
  const { data: programs } = usePrograms()
  const { data: schemes } = useSchemes()
  return useMemo(() => deriveProgress({ measures, programs, schemes }), [measures, programs, schemes])
}

const tooltip = (s: Step, p: StepProgress) =>
  [s.title, p.status === 'unknown' ? '' : (p.detail ?? p.text)].filter(Boolean).join(' – ')

function CheckIcon() {
  return (
    <svg viewBox="0 -960 960 960" className="size-16" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="m382-354 339-339q12-12 28-12t28 12q12 12 12 28.5T777-636L410-268q-12 12-28 12t-28-12L182-440q-12-12-11.5-28.5T183-497q12-12 28.5-12t28.5 12l142 143Z" />
    </svg>
  )
}

/** Step circle: the number, or a check mark once the step has content. */
function StepCircle({ step, done, active }: { step: number; done: boolean; active: boolean }) {
  const filled = done && !active
  return (
    <span
      className="relative inline-flex size-24 shrink-0 items-center justify-center rounded-full border text-kern-16 font-semibold"
      style={{
        borderColor: filled ? 'var(--kern-color-feedback-success)' : 'currentColor',
        background: filled ? 'var(--kern-color-feedback-success)' : undefined,
        color: filled ? 'var(--kern-color-action-on-default)' : undefined,
      }}
      aria-hidden="true"
    >
      {done ? <CheckIcon /> : step}
    </span>
  )
}

/** Below md: one labelled native select instead of the horizontally scrolling list. */
export function StepSelect({ pathname, progress }: { pathname: string; progress: ProcessProgress }) {
  const id = useId()
  const navigate = useNavigate()
  const index = PROCESS_STEPS.findIndex((s) => isActive(pathname, s.path))
  const active = index >= 0 ? PROCESS_STEPS[index] : undefined
  const onOverview = isActive(pathname, OVERVIEW.path)
  return (
    // own wrapper: KERN's unlayered `.kern-form-input { display: flex }` would beat `md:hidden`
    <div className="md:hidden">
      <div className="kern-form-input">
        <label htmlFor={id} className="kern-label">
          {active ? `Schritt ${active.step} von ${PROCESS_STEPS.length}: ${active.label}` : onOverview ? OVERVIEW.label : 'Prozessschritt wählen'}
        </label>
        <div className="kern-form-input__select-wrapper">
          <select
            id={id}
            className="kern-form-input__select"
            value={active?.path ?? (onOverview ? OVERVIEW.path : '')}
            onChange={(e) => {
              if (e.target.value) void navigate(e.target.value)
            }}
          >
            {!active && !onOverview && (
              <option value="" disabled>
                Bitte wählen
              </option>
            )}
            <option value={OVERVIEW.path}>{OVERVIEW.label}</option>
            {PROCESS_STEPS.map((s) => {
              const p = progress[s.step]
              return (
                <option key={s.path} value={s.path}>
                  {`${s.step}. ${s.label}${p.status === 'unknown' ? '' : ` (${p.text})`}`}
                </option>
              )
            })}
          </select>
        </div>
      </div>
    </div>
  )
}

/** Presentational part of the process navigation (testable without queries). */
export function ProcessNavView({ progress }: { progress: ProcessProgress }) {
  const { pathname } = useLocation()
  return (
    <nav aria-label="Prozessschritte" className="no-print">
      <StepSelect pathname={pathname} progress={progress} />
      <ol className="hidden gap-4 overflow-x-auto pb-4 md:flex">
        <li className="relative shrink-0">
          <NavLink
            to={OVERVIEW.path}
            className="dms-focus relative flex items-center gap-8 rounded-kern-default px-12 py-8 no-underline"
            style={{ color: isActive(pathname, OVERVIEW.path) ? 'var(--dms-on-action)' : 'var(--dms-text)' }}
          >
            {isActive(pathname, OVERVIEW.path) && (
              <motion.span
                layoutId="process-nav-active"
                className="absolute inset-0 rounded-kern-default"
                style={{ background: 'var(--dms-action)' }}
                transition={{ type: 'spring', stiffness: 400, damping: 34 }}
              />
            )}
            <span className="relative whitespace-nowrap text-kern-16">{OVERVIEW.label}</span>
          </NavLink>
        </li>
        {PROCESS_STEPS.map((s) => {
          const active = isActive(pathname, s.path)
          const p = progress[s.step]
          return (
            <li key={s.path} className="relative shrink-0">
              <NavLink
                to={s.path}
                title={tooltip(s, p)}
                className="dms-focus relative flex items-center gap-8 rounded-kern-default px-12 py-8 no-underline"
                style={{
                  color: active ? 'var(--dms-on-action)' : 'var(--dms-text)',
                }}
              >
                {active && (
                  <motion.span
                    layoutId="process-nav-active"
                    className="absolute inset-0 rounded-kern-default"
                    style={{ background: 'var(--dms-action)' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                  />
                )}
                <StepCircle step={s.step} done={p.status === 'done'} active={active} />
                <span className="relative whitespace-nowrap text-kern-16">
                  <span className="sr-only">{`Schritt ${s.step}:`}</span> {s.label}
                  {p.status !== 'unknown' && <span className="sr-only">{`, ${p.text}`}</span>}
                </span>
              </NavLink>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export function ProcessNav() {
  return <ProcessNavView progress={useProcessProgress()} />
}
